/**
 * DV-STAGE-003: Storage Versioning and Migration System
 * Handles schema versioning and data migrations for sessionStorage
 */

export interface StorageSchema {
  version: number;
  description: string;
  migrate?: (data: any) => any;
  validate?: (data: any) => boolean;
}

export interface MigrationResult {
  success: boolean;
  fromVersion: number;
  toVersion: number;
  migratedKeys: string[];
  errors: string[];
  skippedKeys: string[];
}

export interface StorageMetadata {
  version: number;
  lastMigration?: string;
  schemaHash?: string;
}

// Current schema version
const CURRENT_VERSION = 2;

// Schema definitions with migration functions
const SCHEMAS: Record<number, StorageSchema> = {
  1: {
    version: 1,
    description: 'Initial DV-STAGE-003 schema',
    validate: (data: any) => {
      return data && typeof data === 'object' &&
             typeof data.source === 'string';
    }
  },

  2: {
    version: 2,
    description: 'Enhanced schema with request tracking and size limits',
    migrate: (data: any) => {
      // Migration from v1 to v2
      if (!data || typeof data !== 'object') return data;

      return {
        ...data,
        version: 2,
        // Add new fields with defaults
        requestIds: data.requestIds || {},
        sizeInfo: data.sizeInfo || {},
        // Rename fields for consistency
        lastOutputRef: data.lastOutputRef || data.lastOutput,
        // Remove deprecated fields
        effectiveInput: undefined,
        promptPreview: undefined
      };
    },
    validate: (data: any) => {
      return data && typeof data === 'object' &&
             data.version === 2 &&
             typeof data.source === 'string';
    }
  }
};

// Storage key patterns for different data types
const STORAGE_PATTERNS = {
  stageTester: /^dv:stageTester:(v\d+:)?(.+)$/,
  sessionOutputs: /^dv:stageTester:sessionOutputs(:(v\d+))?$/,
  metadata: /^dv:storage:metadata$/
};

/**
 * Get storage metadata
 */
function getStorageMetadata(): StorageMetadata {
  try {
    const stored = sessionStorage.getItem('dv:storage:metadata');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.warn('Failed to read storage metadata:', error);
  }

  return { version: 1 }; // Default to v1 for existing data
}

/**
 * Set storage metadata
 */
function setStorageMetadata(metadata: StorageMetadata): void {
  try {
    sessionStorage.setItem('dv:storage:metadata', JSON.stringify(metadata));
  } catch (error) {
    console.warn('Failed to write storage metadata:', error);
  }
}

/**
 * Parse versioned storage key
 */
function parseStorageKey(key: string): { type: string; version?: number; identifier: string } | null {
  // Match stage tester keys
  const stageMatch = key.match(STORAGE_PATTERNS.stageTester);
  if (stageMatch) {
    const versionPart = stageMatch[1]; // "v1:" or undefined
    const identifier = stageMatch[2]; // stage name
    const version = versionPart ? parseInt(versionPart.slice(1, -1)) : 1; // extract number from "v1:"

    return {
      type: 'stageTester',
      version,
      identifier
    };
  }

  // Match session outputs keys
  const outputsMatch = key.match(STORAGE_PATTERNS.sessionOutputs);
  if (outputsMatch) {
    const versionPart = outputsMatch[2]; // "v1" or undefined
    const version = versionPart ? parseInt(versionPart.slice(1)) : 1;

    return {
      type: 'sessionOutputs',
      version,
      identifier: 'global'
    };
  }

  return null;
}

/**
 * Generate versioned storage key
 */
function generateStorageKey(type: string, identifier: string, version: number = CURRENT_VERSION): string {
  switch (type) {
    case 'stageTester':
      return `dv:stageTester:v${version}:${identifier}`;
    case 'sessionOutputs':
      return version === 1 ? 'dv:stageTester:sessionOutputs' : `dv:stageTester:sessionOutputs:v${version}`;
    default:
      throw new Error(`Unknown storage type: ${type}`);
  }
}

/**
 * Migrate data from one version to another
 */
function migrateData(data: any, fromVersion: number, toVersion: number): any {
  let migrated = data;

  // Apply migrations sequentially
  for (let version = fromVersion + 1; version <= toVersion; version++) {
    const schema = SCHEMAS[version];
    if (schema?.migrate) {
      migrated = schema.migrate(migrated);
    }
  }

  return migrated;
}

/**
 * Validate data against schema
 */
function validateData(data: any, version: number): boolean {
  const schema = SCHEMAS[version];
  if (schema?.validate) {
    return schema.validate(data);
  }
  return true; // No validation available, assume valid
}

/**
 * Migrate all storage data to current version
 */
export function migrateStorage(): MigrationResult {
  const result: MigrationResult = {
    success: true,
    fromVersion: 1,
    toVersion: CURRENT_VERSION,
    migratedKeys: [],
    errors: [],
    skippedKeys: []
  };

  const metadata = getStorageMetadata();
  result.fromVersion = metadata.version;

  // Skip if already current version
  if (metadata.version >= CURRENT_VERSION) {
    return result;
  }

  console.log(`= Migrating storage from v${metadata.version} to v${CURRENT_VERSION}`);

  // Get all keys to migrate
  const allKeys = Object.keys(sessionStorage);
  const keysToMigrate = allKeys.filter(key => parseStorageKey(key) !== null);

  for (const key of keysToMigrate) {
    try {
      const parsed = parseStorageKey(key);
      if (!parsed) continue;

      const { type, version, identifier } = parsed;

      // Skip if already current version
      if (version === CURRENT_VERSION) {
        result.skippedKeys.push(key);
        continue;
      }

      // Read existing data
      const rawData = sessionStorage.getItem(key);
      if (!rawData) {
        result.skippedKeys.push(key);
        continue;
      }

      let data;
      try {
        data = JSON.parse(rawData);
      } catch {
        result.errors.push(`Failed to parse JSON for key: ${key}`);
        continue;
      }

      // Migrate data
      const migratedData = migrateData(data, version || 1, CURRENT_VERSION);

      // Validate migrated data
      if (!validateData(migratedData, CURRENT_VERSION)) {
        result.errors.push(`Validation failed for migrated key: ${key}`);
        continue;
      }

      // Generate new key and store migrated data
      const newKey = generateStorageKey(type, identifier, CURRENT_VERSION);
      sessionStorage.setItem(newKey, JSON.stringify(migratedData));

      // Remove old key if different
      if (newKey !== key) {
        sessionStorage.removeItem(key);
      }

      result.migratedKeys.push(key);

    } catch (error) {
      result.errors.push(`Migration failed for key ${key}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      result.success = false;
    }
  }

  // Update metadata
  setStorageMetadata({
    version: CURRENT_VERSION,
    lastMigration: new Date().toISOString(),
    schemaHash: generateSchemaHash()
  });

  const summary = {
    migrated: result.migratedKeys.length,
    skipped: result.skippedKeys.length,
    errors: result.errors.length
  };

  console.log(` Storage migration complete:`, summary);

  return result;
}

/**
 * Generate schema hash for validation
 */
function generateSchemaHash(): string {
  const schemaString = JSON.stringify(SCHEMAS, Object.keys(SCHEMAS).sort());

  // Simple hash (not cryptographic)
  let hash = 0;
  for (let i = 0; i < schemaString.length; i++) {
    const char = schemaString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return Math.abs(hash).toString(36);
}

/**
 * Get versioned storage key for stage tester data
 */
export function getStageTesterKey(stage: string): string {
  return generateStorageKey('stageTester', stage, CURRENT_VERSION);
}

/**
 * Get versioned storage key for session outputs
 */
export function getSessionOutputsKey(): string {
  return generateStorageKey('sessionOutputs', 'global', CURRENT_VERSION);
}

/**
 * Store data with automatic versioning
 */
export function setVersionedItem(type: string, identifier: string, data: any): void {
  const key = generateStorageKey(type, identifier, CURRENT_VERSION);
  const versionedData = {
    ...data,
    version: CURRENT_VERSION,
    updatedAt: new Date().toISOString()
  };

  try {
    sessionStorage.setItem(key, JSON.stringify(versionedData));
  } catch (error) {
    console.warn(`Failed to store ${type}:${identifier}:`, error);
  }
}

/**
 * Get data with automatic migration if needed
 */
export function getVersionedItem(type: string, identifier: string): any | null {
  const currentKey = generateStorageKey(type, identifier, CURRENT_VERSION);

  // Try current version first
  try {
    const current = sessionStorage.getItem(currentKey);
    if (current) {
      const data = JSON.parse(current);
      if (validateData(data, CURRENT_VERSION)) {
        return data;
      }
    }
  } catch (error) {
    console.warn(`Failed to read current version of ${type}:${identifier}:`, error);
  }

  // Try to find and migrate older versions
  const allKeys = Object.keys(sessionStorage);
  for (const key of allKeys) {
    const parsed = parseStorageKey(key);
    if (parsed && parsed.type === type && parsed.identifier === identifier && parsed.version !== CURRENT_VERSION) {
      try {
        const rawData = sessionStorage.getItem(key);
        if (!rawData) continue;

        const data = JSON.parse(rawData);
        const migrated = migrateData(data, parsed.version || 1, CURRENT_VERSION);

        if (validateData(migrated, CURRENT_VERSION)) {
          // Store migrated version
          setVersionedItem(type, identifier, migrated);

          // Remove old version
          sessionStorage.removeItem(key);

          return migrated;
        }
      } catch (error) {
        console.warn(`Failed to migrate ${key}:`, error);
      }
    }
  }

  return null;
}

/**
 * Clean up stale storage data
 */
export function cleanupStorage(maxAgeMs: number = 7 * 24 * 60 * 60 * 1000): number {
  const now = Date.now();
  const allKeys = Object.keys(sessionStorage);
  let removedCount = 0;

  for (const key of allKeys) {
    const parsed = parseStorageKey(key);
    if (!parsed) continue;

    try {
      const rawData = sessionStorage.getItem(key);
      if (!rawData) continue;

      const data = JSON.parse(rawData);
      const updatedAt = data.updatedAt || data.fetchedAt;

      if (updatedAt) {
        const age = now - new Date(updatedAt).getTime();
        if (age > maxAgeMs) {
          sessionStorage.removeItem(key);
          removedCount++;
        }
      }
    } catch (error) {
      // Remove corrupted entries
      sessionStorage.removeItem(key);
      removedCount++;
    }
  }

  return removedCount;
}

/**
 * Get storage usage statistics
 */
export function getStorageStats(): {
  totalKeys: number;
  versionedKeys: number;
  currentVersionKeys: number;
  oldVersionKeys: number;
  sizeKB: number;
} {
  const allKeys = Object.keys(sessionStorage);
  const versionedKeys = allKeys.filter(key => parseStorageKey(key) !== null);
  const currentVersionKeys = versionedKeys.filter(key => {
    const parsed = parseStorageKey(key);
    return parsed?.version === CURRENT_VERSION;
  });

  let totalSize = 0;
  for (const key of allKeys) {
    const value = sessionStorage.getItem(key);
    if (value) {
      totalSize += key.length + value.length;
    }
  }

  return {
    totalKeys: allKeys.length,
    versionedKeys: versionedKeys.length,
    currentVersionKeys: currentVersionKeys.length,
    oldVersionKeys: versionedKeys.length - currentVersionKeys.length,
    sizeKB: Math.round(totalSize / 1024 * 100) / 100
  };
}

// Initialize migration on module load
if (typeof window !== 'undefined') {
  try {
    migrateStorage();
  } catch (error) {
    console.warn('Failed to initialize storage migration:', error);
  }
}

